import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { openCookieSettings } from '@/lib/consent';

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const { isDark } = useTheme();
  const badgeSlotRef = useRef(null);

  /**
   * Relocate the platform-injected "Made with Emergent" badge from its
   * fixed bottom-right position into our footer slot. This runs only on
   * desktop (>= 769px) — on mobile the existing CSS in index.css keeps
   * it as a tiny floating circle.
   *
   * The DOM node is owned by the platform and may be (re)injected after
   * route changes, so we observe the body for late insertions.
   */
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const moveBadge = () => {
      const badge = document.getElementById('emergent-badge');
      if (!badge || !badgeSlotRef.current) return;
      // Always relocate the badge inside the footer slot — both on
      // desktop and mobile — so it never floats over content,
      // navigation or the reading area.
      if (badge.parentElement !== badgeSlotRef.current) {
        badgeSlotRef.current.appendChild(badge);
        badge.setAttribute('data-in-footer', 'true');
      }
      // Beat the platform's inline !important styles by setting our own
      // !important values via setProperty (equal specificity, wins by order).
      // Remove the "hide-at-first-paint" overrides set by the inline script
      // in /public/index.html so our CSS rule for [data-in-footer] takes over.
      badge.style.removeProperty('visibility');
      badge.style.removeProperty('opacity');
      badge.style.removeProperty('pointer-events');
      badge.style.setProperty('position', 'static', 'important');
      badge.style.setProperty('bottom', 'auto', 'important');
      badge.style.setProperty('right', 'auto', 'important');
      badge.style.setProperty('top', 'auto', 'important');
      badge.style.setProperty('left', 'auto', 'important');
      badge.style.setProperty('margin', '0', 'important');
      badge.style.setProperty('padding', '3px 9px', 'important');
      badge.style.setProperty('width', 'auto', 'important');
      badge.style.setProperty('height', 'auto', 'important');
      badge.style.setProperty('opacity', '0.55', 'important');
      badge.style.setProperty('font-size', '10px', 'important');
      badge.style.setProperty('box-shadow', 'none', 'important');
      badge.style.setProperty('transform', 'none', 'important');
      badge.style.setProperty('vertical-align', 'middle', 'important');
    };

    moveBadge();

    // Re-attempt on resize and on dynamic re-injection by the platform
    const observer = new MutationObserver(moveBadge);
    observer.observe(document.body, { childList: true });
    window.addEventListener('resize', moveBadge);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', moveBadge);
    };
  }, []);

  return (
    <footer className="bg-surface border-t border-border mt-auto">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="col-span-1 md:col-span-2">
            <Link to="/" className="inline-block">
              <img 
                src={isDark ? '/logo-white.png' : '/logo-red.png'} 
                alt="Intimate.pt" 
                className="h-10 w-auto"
              />
            </Link>
            <p className="mt-3 text-sm text-muted-foreground max-w-md">
              Plataforma de leitura erótica para adultos. Contos, séries e muito mais 
              para os seus momentos de prazer literário.
            </p>
            <p className="mt-4 text-xs text-muted-foreground">
              Conteúdo exclusivo para maiores de 18 anos.
            </p>
          </div>
          
          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-foreground mb-4">Explorar</h3>
            <ul className="space-y-2">
              <li>
                <Link 
                  to="/browse" 
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Contos
                </Link>
              </li>
              <li>
                <Link 
                  to="/series" 
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Séries
                </Link>
              </li>
              <li>
                <Link 
                  to="/confessions" 
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Confissões
                </Link>
              </li>
              <li>
                <Link 
                  to="/mais-prazer" 
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  + Prazer
                </Link>
              </li>
            </ul>
          </div>
          
          {/* Legal */}
          <div>
            <h3 className="font-semibold text-foreground mb-4">Legal</h3>
            <ul className="space-y-2">
              <li>
                <Link 
                  to="/terms" 
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="footer-terms-link"
                >
                  Termos de Utilização
                </Link>
              </li>
              <li>
                <Link 
                  to="/privacy" 
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="footer-privacy-link"
                >
                  Política de Privacidade
                </Link>
              </li>
              <li>
                <Link 
                  to="/cookies" 
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="footer-cookies-link"
                >
                  Política de Cookies
                </Link>
              </li>
              <li>
                <button
                  type="button"
                  onClick={openCookieSettings}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="footer-cookie-settings"
                >
                  Definições de cookies
                </button>
              </li>
            </ul>
          </div>
        </div>
        
        {/* Bottom Bar */}
        <div className="mt-8 pt-8 border-t border-border flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs text-muted-foreground">
            © {currentYear} Intimate.pt. Todos os direitos reservados.
          </p>
          <div className="flex items-center gap-6 text-xs text-muted-foreground">
            <span>Contacto: geral@intimate.pt</span>
            {/* Host slot for the platform badge on desktop */}
            <span
              ref={badgeSlotRef}
              data-testid="footer-platform-credit"
              className="footer-platform-credit inline-flex items-center"
              aria-label="Credito da plataforma"
            />
          </div>
        </div>
      </div>
    </footer>
  );
}
